import { useEffect, useRef, useState } from "react";
import { Loader2, Mic, Square, X } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

const MAX_MS = 60000;
const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

const startLive = (onText) => {
  if (!SR) return null;
  const r = new SR();
  r.lang = "en-IN";
  r.continuous = true;
  r.interimResults = true;
  r.onresult = (e) => onText(Array.from(e.results).map((x) => x[0].transcript).join(" ").trim());
  r.onerror = () => {};
  try { r.start(); } catch { return null; }
  return r;
};

export const NotesInput = ({ value, onChange, voiceAvailable }) => {
  const [state, setState] = useState("idle");
  const [live, setLive] = useState("");
  const recRef = useRef(null);
  const srRef = useRef(null);
  const liveRef = useRef("");
  const cancelRef = useRef(false);
  const timerRef = useRef(null);
  const canVoice = voiceAvailable || !!SR;

  useEffect(() => () => {
    cancelRef.current = true;
    recRef.current?.state === "recording" && recRef.current.stop();
    srRef.current?.abort();
    clearTimeout(timerRef.current);
  }, []);

  const append = (t) => onChange((value ? `${value} ` : "") + t);
  const setLiveText = (t) => { liveRef.current = t; setLive(t); };

  const finish = async (chunks, type) => {
    const draft = liveRef.current;
    setLiveText("");
    if (!voiceAvailable) {
      draft ? append(draft) : toast.error("We didn't catch any speech. Please try again.");
      return setState("idle");
    }
    setState("transcribing");
    const form = new FormData();
    form.append("file", new Blob(chunks, { type }), `voice.${type.split("/")[1]}`);
    form.append("hint", draft.slice(0, 200));
    try {
      const { data } = await api.post("/transcribe-audio", form, { timeout: 60000 });
      append(data.text);
      toast.success("Transcribed — review and edit before generating.");
    } catch (e) {
      if (draft) { append(draft); toast.message("Used your device's quick transcript — please check it."); }
      else toast.error(errMsg(e, "Couldn't transcribe. Please type instead."));
    } finally {
      setState("idle");
    }
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || (!window.MediaRecorder && !SR)) {
      toast.error("Voice input isn't supported in this browser. Please type instead.");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("Microphone access was denied. You can type your note instead.");
      return;
    }
    cancelRef.current = false;
    setLiveText("");
    srRef.current = startLive(setLiveText);
    const mime = window.MediaRecorder ? ["audio/webm", "audio/mp4", "audio/ogg"].find((t) => MediaRecorder.isTypeSupported?.(t)) || "" : "";
    const rec = window.MediaRecorder ? new MediaRecorder(stream, mime ? { mimeType: mime } : undefined) : null;
    const chunks = [];
    const done = () => {
      stream.getTracks().forEach((t) => t.stop());
      srRef.current?.stop();
      clearTimeout(timerRef.current);
      if (cancelRef.current) { setLiveText(""); return setState("idle"); }
      finish(chunks, ((rec && rec.mimeType) || "audio/webm").split(";")[0]);
    };
    if (rec) {
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => setTimeout(done, 300);
      rec.start();
    }
    recRef.current = rec || { state: "recording", stop: () => { recRef.current.state = "inactive"; setTimeout(done, 300); } };
    setState("recording");
    timerRef.current = setTimeout(() => recRef.current?.state === "recording" && recRef.current.stop(), MAX_MS);
  };

  const stop = () => recRef.current?.stop();
  const cancel = () => { cancelRef.current = true; recRef.current?.stop(); };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label htmlFor="situation-notes" className="text-sm font-semibold">Situation notes <span className="font-normal text-muted-foreground">(optional)</span></label>
        {canVoice
          ? <span className="text-[11px] text-muted-foreground" data-testid="voice-hint">Bolo Hindi, English ya Hinglish mein</span>
          : <span className="text-[11px] text-muted-foreground" data-testid="voice-unavailable">Voice unavailable</span>}
      </div>
      <div className="relative">
        <Textarea id="situation-notes" data-testid="situation-notes-input" value={value} onChange={(e) => onChange(e.target.value)} maxLength={500} rows={2}
          placeholder="e.g. Make it flirty but not too obvious. We met at a friend's party."
          className="rounded-xl text-sm pr-14" />
        {canVoice && state === "idle" && (
          <button data-testid="voice-input-mic-button" onClick={start} aria-label="Record voice note" className="absolute right-2 top-2 h-9 w-9 rounded-full bg-secondary hover:bg-primary hover:text-white flex items-center justify-center transition-colors"><Mic className="h-4 w-4" /></button>
        )}
        {state === "transcribing" && <Loader2 data-testid="voice-transcribing" className="absolute right-4 top-4 h-4 w-4 animate-spin text-primary" />}
      </div>
      {state === "recording" && (
        <div className="space-y-2" data-testid="voice-recording-indicator">
          <div className="flex items-center gap-2 text-sm">
            <span className="h-3 w-3 rounded-full bg-accent pulse-ring" /> Listening… (max 60s)
            <button data-testid="voice-stop-button" onClick={stop} className="ml-auto h-8 px-3 rounded-full bg-primary text-white text-xs font-semibold inline-flex items-center gap-1"><Square className="h-3 w-3" />Done</button>
            <button data-testid="voice-cancel-button" onClick={cancel} className="h-8 px-3 rounded-full border border-border text-xs inline-flex items-center gap-1"><X className="h-3 w-3" />Cancel</button>
          </div>
          {live && <p data-testid="voice-live-transcript" className="text-xs italic text-muted-foreground rounded-lg bg-secondary/50 px-3 py-2">{live}</p>}
        </div>
      )}
    </div>
  );
};
